import * as React from "react";
import { TextBox } from "../../forms/informedv2/TextBox";
import { Required } from "../../forms/validation/Required";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import { TextArea } from "../../forms/informedv2/TextArea";
import { DragDrop } from "../../misc/DragDrop";
import { FileRejection } from "react-dropzone";
import { MultiSelect } from "../../forms/informedv2/MultiSelect";
import { DeviceType, StudyLanguage, StudyCountry, Country } from "webportalclient";
import { MetricsForm } from "./StudyMetricsForm";
import { OverreadForm } from "./StudyOverreadForm";

export interface StudyForm extends MetricsForm, StudyCoreForm, OverreadForm {

}

export interface StudyCoreForm {
    Id?: string;
    Name: string;
    Protocol: string;
    Countries: Country[];
    DeviceTypes: DeviceType[];
    DiaryLanguage: StudyLanguage[];
    Description: string;
    Logo: string;
}

export interface StudyFormProps {
    onAlert: (alert: string) => void;
    onFileAttached: (file: File) => void;
}

export const ExtractBase64Image = async (logoToUpload: Blob) => {
    const arrayBuffer = await logoToUpload.arrayBuffer();
    let binaryString = '';
    let bytes = new Uint8Array(arrayBuffer);
    let length = bytes.byteLength;
    for (let i = 0; i < length; i++) {
        binaryString += String.fromCharCode(bytes[i]);
    }

    return btoa(binaryString);
}

export const StudyFormComponent = ({ onAlert, onFileAttached }: StudyFormProps) => {
    const { countries, deviceTypes, languages } = useReferenceContextProvider();
    
    const [base64Logo, setBase64Logo] = React.useState<string>();

    const onDrop = async (accepted: File[], rejected: FileRejection[]) => {
        onAlert(null);

        if (rejected.length > 0) {
            onAlert(
                `${rejected[0].file.name} - ${rejected[0].errors.map(e => e.message).join(", ")}`
            );
            return;
        }

        onFileAttached(accepted[0]);
        const reader = new FileReader();
        reader.readAsDataURL(accepted[0]);
        reader.onloadend = () => {
            setBase64Logo(reader.result as string);
        }
    };

    return (
        <>
            <div className="row">
                <div className="form-group col-md-4 col-sm-4 col-xs-12">
                    <label htmlFor="Name">Name</label>
                    <TextBox<string, StudyCoreForm>
                        id="Name"
                        field="Name"
                        validate={Required}
                    />
                </div>
                <div className="form-group col-md-4 col-sm-4 col-xs-12">
                    <label htmlFor="Name">Protocol Number</label>
                    <TextBox<string, StudyCoreForm>
                        id="Protocol"
                        field="Protocol"
                        validate={Required}
                    />
                </div>
            </div>
            <div className="row">
                <div className="form-group col-md-4 col-sm-4 col-xs-12">
                    <label htmlFor="Name">Countries</label>
                    <MultiSelect
                        id="Countries"
                        field="Countries"
                        labelKey="name"
                        valueKey="id"
                        multi
                        options={countries}
                        validate={Required}
                    />
                </div>
                <div className="form-group col-md-4 col-sm-4 col-xs-12">
                    <label htmlFor="Name">Device Types</label>
                    <MultiSelect
                        id="DeviceTypes"
                        field="DeviceTypes"
                        labelKey="name"
                        valueKey="id"
                        multi
                        options={deviceTypes}
                        validate={Required}
                    />
                </div>
                <div className="form-group col-md-4 col-sm-4 col-xs-12">
                    <label htmlFor="Name">E-Diary Language</label>
                    <MultiSelect
                        id="DiaryLanguage"
                        field="DiaryLanguage"
                        labelKey="name"
                        valueKey="localeName"
                        multi
                        options={languages}
                    />
                </div>
            </div>
            <div className="row">
                <div className="form-group col-md-12 col-sm-12 col-xs-12">
                    <label htmlFor="Name">Description</label>
                    <TextArea
                        id="Description"
                        field="Description"
                    />
                </div>
            </div>
            <div className="row">
                <div className="form-group col-md-6 col-sm-6 col-xs-12">
                    <label htmlFor="Logo">Logo</label>
                    <DragDrop
                        id="Logo"
                        options={{
                            onDrop,
                            maxFiles: 1,
                            maxSize: 500000,
                            accept: {
                                "image/jpg": [".jpg", ".jpeg"],
                                "image/png": [".png"],
                                "image/gif": [".gif"],
                            }
                        }}
                    />
                </div>
                {base64Logo ? <div className="col-md-6 col-sm-6 col-xs-12">
                    <img style={{ maxWidth: 100 + '%' }} src={base64Logo} alt="Study logo" />
                </div> : null}
            </div>
        </>
    );
}
